import { Pith } from "pith";
import {
    Bundle,
    CommandContext,
    LayoutsState,
    persistenceService,
    SpiffCommerceClient,
    TextStepHandle,
} from "../index";
import { graphQlManager } from "../services/server";
import { StepHandle } from "../stepHandles";
import {
    Product,
    DesignCreationMessage,
    Transaction,
    CustomerDetailsInput,
    Workflow,
    ILayout,
    AnyStepData,
    StepType,
    LayoutRenderingPurpose,
    GlobalPropertyAspectConfiguration,
    Step,
} from "../types";
import {
    WorkflowManager,
    DesignCreationProgressUpdate,
    InternalWorkflowManager,
    StateMutationFunc,
} from "../WorkflowManager";
import { StepHandleFactory } from "../stepHandles/factory";
import { designService, createDesign, SavedDesign } from "../services/design";
import cloneDeep from "lodash.clonedeep";
import { ResourceGenerationError, UnhandledBehaviorError } from "../util/exception";
import debounce from "lodash.debounce";
import { RenderableScene, stepConditionsAreSatisfied } from "../WorkflowManager/scenes";
import type { ModelContainer, ThreeDPreviewService } from "@spiffcommerce/preview";
import { LayoutPreviewService } from "../WorkflowManager/LayoutPreviewService";
import { gql } from "@apollo/client/core";
import { finalizeUpdateTransactionMutation, updateTransactionQuantityQuery } from "../query";
import { createCanvas, getDomParser, loadImage } from "../customCanvas";
import { getSvgElement } from "../CommandContext";
import { renderPapyrusComponentAsString } from "../util/crossplatform";
import { GraphQlClientFunc } from "../services/graphql";

const addTransactionStakeholderMutation = gql`
    mutation AddTransactionStakeholder($id: String!, $type: String!, $details: CustomerDetailsInput!) {
        transactionAddStakeholder(id: $id, details: $details, type: $type) {
            id
            stakeholders {
                id
                type
                customer {
                    id
                    emailAddress
                }
            }
        }
    }
`;

const addAddressToTransactionMutation = gql`
    mutation AddAddressToTransaction(
        $transactionId: String!
        $streetAddress: String
        $apartment: String
        $city: String
        $country: String
        $state: String
        $postCode: String
    ) {
        addressAttachToTransaction(
            transactionId: $transactionId
            streetAddress: $streetAddress
            apartment: $apartment
            city: $city
            country: $country
            state: $state
            postCode: $postCode
        ) {
            id
        }
    }
`;

const addOrganizationToTransactionMutation = gql`
    mutation AddOrganizationToTransaction($transactionId: String!, $organizationName: String!) {
        organizationAttachToTransaction(transactionId: $transactionId, organizationName: $organizationName) {
            id
        }
    }
`;

/**
 * A scene is a collection of steps that can be used to group steps together.
 */
export interface Scene {
    /**
     * The unique identifier for the scene.
     */
    id?: string;
    /**
     * The name of the scene.
     */
    name: string;
    /**
     * The steps that are part of the scene. A list of ids. See getStepById.
     */
    stepIds: string[];
}

/**
 * State related to a workflow experience.
 */
export interface ExperienceOptions {
    product: Product;
    transaction: Transaction;
    workflow?: Workflow;
    /**
     * @deprecated favor inject / eject functions.
     */
    previewService?: ThreeDPreviewService;
    modelContainer?: ModelContainer;
    renderableContextService?: LayoutPreviewService;
    layouts: ILayout[];
    reloadedState?: LayoutsState;
    /**
     * When true the experience is intended to be immutable.
     */
    readOnly?: boolean;
    /**
     * A function that communicates state changes to the server.
     */
    stateMutationFunc: StateMutationFunc;
    /**
     * The interface for the graphql client
     */
    graphQlClient: GraphQlClientFunc;
    /**
     * Should be set to true when the experience is loaded from an existing transaction.
     * FIXME: Wouldn't we know this from existance of reloadedState
     */
    isReloadedTransaction?: boolean;
    /**
     * When true the system will treat steps with
     * a single variant as renderable. False
     * by default.
     */
    singleVariantsRenderable?: boolean;

    /**
     * When true, will delay syncing the workflow state until manually enabled.
     */
    delayWorkflowStateSync?: boolean;
}

/**
 * A Workflow experience encapsulates the workflow manager and command context. It
 * provides a simplified interface for interacting with the workflow manager. You
 * should get an instance of this class from a Client you have constructed previously.
 */
export interface WorkflowExperience {
    /**
     * Get the bundle this experience is part of. May be undefined.
     */
    getBundle(): Bundle | undefined;

    /**
     * Set the bundle this experience is part of. Can be cleared using undefined.
     */
    setBundle(bundle: Bundle | undefined);

    /**
     * Returns the client that was responsible for spawning this experience.
     */
    getClient(): SpiffCommerceClient;
    /**
     * State related to the design of the user.
     */
    getCommandContext(): CommandContext;
    /**
     * Returns true when the user may only view the design.
     */
    getIsReadOnly(): boolean;
    /**
     * Get the low level workflow amanager instance for this experience. Don't touch this unless you're willing to break things.
     */
    getWorkflowManager(): WorkflowManager;

    /**
     * Returns the step matching a given name, undefined if not found.
     * @param id The id the step must match.
     */
    getStepById(id: string): StepHandle<AnyStepData> | undefined;

    /**
     * Returns the step matching a given name, undefined if not found.
     * @param name The name the step must match.
     */
    getStepByName(name: string): StepHandle<AnyStepData> | undefined;

    /**
     * Returns all steps matching a specific type in the workflow. These steps
     * may be across multiple scenes and may or may not be active based on condition state.
     */
    getStepsByType(type: StepType): StepHandle<AnyStepData>[];

    /**
     * Returns all steps that are children of a given scene.
     * @param scene The scene you want the steps for.
     */
    getStepsByScene(scene: Scene): StepHandle<AnyStepData>[];

    /**
     * Returns all steps in the workflow. Ordered by scene and appearance within their respective scenes.
     */
    getSteps(): StepHandle<AnyStepData>[];

    /**
     * Returns all steps in the workflow that are conditionally active. Ordered by scene and appearance within their respective scenes.
     */
    getStepsConditionallyActive(): StepHandle<AnyStepData>[];

    /**
     * Returns a list of scenes that are configured in the workflow. Each scene
     * contains a list of steps. See getStepsByScene to access these.
     */
    getScenes(): Scene[];

    /**
     * Returns the total cost in subunits for all selections made on the design.
     * @param disablePriceBreaks Whether to exclude price breaks from the calculation.
     */
    getSelectionPriceSubunits(disablePriceBreaks?: boolean): number;

    /**
     * Returns the total cost in subunits for the base product.
     * @param includeAdditionalProduct When true the additional product cost will be included in the total (if configured).
     * @param disablePriceBreaks Whether to exclude price breaks from the calculation.
     */
    getBasePriceSubunits(includeAdditionalProduct?: boolean, disablePriceBreaks?: boolean): number;

    /**
     * If an additional product is configured, returns the base price of that product. Returns undefined otherwise.
     * @param disablePriceBreaks Whether to exclude price breaks from the calculation.
     */
    getAdditionalProductPriceSubunits(disablePriceBreaks?: boolean): number | undefined;

    /**
     * A convenience function returning the sum of the selection and base price values.
     * @param disablePriceBreaks Whether to exclude price breaks from the calculation.
     */
    getTotalPriceSubunits(disablePriceBreaks?: boolean): number;

    /**
     * The price break percentage that is expected to be applied in price calculations.
     */
    priceBreakToBeApplied(): number;

    /**
     * Calculates the price break and fires the "PriceBreakChanged" event if required.
     * This function is primarily intended for internal use.
     */
    checkForPriceBreakChanges(): void;

    /**
     * Takes selections made by the user in another workflow and applies them to this workflow. For
     * selections to be copied they must both have a matching global property configuration.
     * @param experience The experience to take selections from.
     * @param filter A list of steps to apply the selections to. If undefined all steps will be updated.
     */
    copySelectionsViaGlobalConfiguration(
        bundle: Bundle,
        experience: WorkflowExperience,
        filter?: StepHandle<AnyStepData>[],
    ): Promise<void>;

    /**
     * Attach specific details about the customer to the experience. This is useful for things like retargeting. Currently only
     * email is supported. From SpiffCommerce hosted experiences these details will be attached whenever the customer has provided & given permission.
     * @param details The new customer details. Only email is supported.
     * @deprecated Use assignCustomerDetails instead.
     */
    attachCustomerDetails(details: {
        /**
         * An email used for things like sending a design to the user.
         */
        email: string;
    }): Promise<void>;

    /**
     * Attach specific details about the customer to the experience. This is useful for things like retargeting.
     * From SpiffCommerce hosted experiences these details will be attached whenever the customer has provided & given permission.
     * @param details The new customer details.
     */
    assignCustomerDetails(details: CustomerDetailsInput): Promise<void>;

    /**
     * Attaches a listener to the scenes on a workflow experience. The scenes returned are a subset of the scenes configured in the
     * workflow and are based on the current state of the experience. This is useful for building a navigation menu.
     * @param cb The callback to be called when the scenes change. The new scenes are passed as an argument.
     */
    attachRenderableSceneListener(cb: (scenes: RenderableScene[]) => void): void;

    /**
     * Detaches a listener from the scenes on a workflow experience.
     */
    detachRenderableSceneListener(cb: (scenes: RenderableScene[]) => void): void;

    /**
     * Saves this experience to storage. This may be local or remote depending
     * on configuration.
     * @param title The title for the saved design.
     */
    save(title: string): Promise<SavedDesign>;

    /**
     * Returns a copy of the currently loaded design as a new, seperated workflow experience with
     * a fresh transaction.
     */
    copy(): Promise<WorkflowExperience>;

    /**
     * Creates a data URL preview for the current design.
     */
    createPreviewImage(isThreeD?: boolean, resolution?: number): Promise<string>;

    /**
     * To be called when the workflow experience is considered completed by the user.
     * @param onProgressUpdate Progress callback for finalizing the design. Optional
     * @param capturePreviewImage When true a preview image will be generated for the design. Defaults to true.
     */
    onDesignFinished(
        onProgressUpdate?: DesignCreationProgressUpdate,
        capturePreviewImage?: boolean,
    ): Promise<DesignCreationMessage>;

    /**
     * Returns the metadata associated with this workflow experience.
     * This is a combination of the metadata from the workflow, and the selections made by the user.
     */
    getExportedData(): Map<string, { [key: string]: string }>;

    /**
     * Get the quantity of this WorkflowExperience's Transaction.
     * @returns The amount that was, or will be, ordered.
     */
    getQuantity(): number;

    /**
     * Sets the quantity of this WorkflowExperience's Transaction.
     * @throws {Error} if the WorkflowExperience is read-only.
     * @throws {RangeError} if the value is below 1.
     * @param quantity The amount that was, or will be, ordered.
     * @returns A promise that resolves when the Transaction has been updated on the server.
     */
    setQuantity(quantity: number): Promise<void>;

    /**
     * Registers a callback function to be called when the specified event is raised.
     * @param type The type of event to listen for.
     * @param callback The function to call when the event occurs.
     */
    addEventListener(
        type: WorkflowExperienceEventType,
        callback: (workflowExperience: WorkflowExperience) => void,
    ): void;

    /**
     * Removes a previously registered callback.
     * @param type The type of event.
     * @param callback The function to remove.
     */
    removeEventListener(
        type: WorkflowExperienceEventType,
        callback: (workflowExperience: WorkflowExperience) => void,
    ): void;

    attachAddress(
        streetAddress?: string,
        apartment?: string,
        city?: string,
        country?: string,
        state?: string,
        postCode?: string,
    ): Promise<void>;

    attachOrganization(name: string): Promise<void>;
}

export enum WorkflowExperienceEventType {
    QuantityChanged = "QuantityChanged",
    PriceBreakChanged = "PriceBreakChanged",
}

/**
 * Different types of aspect of a step that can be configured to be the target of global properties
 */
enum AspectType {
    FileUpload = "FileUpload",
    Option = "Option",
    Text = "Text",
}

export class WorkflowExperienceImpl implements WorkflowExperience {
    readonly client: SpiffCommerceClient;
    readonly commandContext: CommandContext;
    readonly graphQlClient: GraphQlClientFunc;
    readonly workflowManager: WorkflowManager;
    readonly isReadOnly: boolean;
    readonly cachedStepHandles: Map<string, StepHandle<any>> = new Map();

    /**
     * Bundle this experience has been added to.
     */
    private bundle?: Bundle;
    private currentPriceBreak = 1;

    // A list of scenes that currently available to the user. This is a subset of the scenes available in the workflow.
    // The steps contained within are also a subset.
    private renderableScenes: RenderableScene[] = [];
    private renderableSceneCallbacks: ((scenes: RenderableScene[]) => void)[] = [];

    private eventCallbacks: Map<WorkflowExperienceEventType, ((workflowExperience: WorkflowExperience) => void)[]> =
        new Map();

    constructor(client: SpiffCommerceClient, experienceOptions: ExperienceOptions) {
        if (!experienceOptions.workflow) {
            throw new Error("No Workflow ID provided.");
        }

        this.client = client;

        const layouts = experienceOptions.layouts;

        this.commandContext = new CommandContext();
        this.commandContext.initialize(layouts, experienceOptions.reloadedState);

        this.isReadOnly = experienceOptions.transaction.isOrdered || !!experienceOptions.readOnly;
        this.graphQlClient = experienceOptions.graphQlClient;

        this.workflowManager = new InternalWorkflowManager(
            this,
            client,
            experienceOptions.workflow,
            experienceOptions.product.profanities?.map((x) => x.word) || [],
            layouts,
            this.commandContext,
            (request) => {
                try {
                    this.debouncedSavedDesignUpdate();
                } catch {
                    console.error("Failed to update saved design details.");
                }
                return experienceOptions.stateMutationFunc(request);
            },
            experienceOptions.graphQlClient,
            experienceOptions.transaction,
            experienceOptions.product,
            experienceOptions.previewService,
            experienceOptions.renderableContextService,
            experienceOptions.reloadedState,
            experienceOptions.readOnly,
            experienceOptions.modelContainer,
            experienceOptions.isReloadedTransaction,
            experienceOptions.singleVariantsRenderable,
            experienceOptions.delayWorkflowStateSync,
        );

        // We attach a listener to the manager and update the available scenes when they change. We store simplified information about the scenes
        // instead of the full complex structure. Internal scene state/configuration should be considered private & exposed through handles instead.
        // The user can get reference to a handle via the step ID returned here.
        this.workflowManager.addSelectionCallback((callbackOptions) => {
            const newScenes = callbackOptions.traversableScenes.map((workflowScene) => {
                const newSteps = workflowScene.renderableSteps.map((step) => step.stepName);
                return {
                    id: workflowScene.name,
                    title: workflowScene.title,
                    renderableSteps: newSteps,
                    workflowScene,
                } as RenderableScene;
            });
            this.renderableScenes = newScenes;
            this.renderableSceneCallbacks.forEach((cb) => cb(newScenes));
        });

        this.currentPriceBreak = this.priceBreakToBeApplied();
    }

    async attachAddress(
        streetAddress?: string,
        apartment?: string,
        city?: string,
        country?: string,
        state?: string,
        postCode?: string,
    ): Promise<void> {
        await this.graphQlClient().mutate({
            mutation: addAddressToTransactionMutation,
            variables: {
                transactionId: this.getWorkflowManager().getTransaction().id,
                streetAddress: streetAddress || undefined,
                apartment: apartment || undefined,
                city: city || undefined,
                country: country || undefined,
                state: state || undefined,
                postCode: postCode || undefined,
            },
        });
    }

    async attachOrganization(name: string): Promise<void> {
        await this.graphQlClient().mutate({
            mutation: addOrganizationToTransactionMutation,
            variables: {
                transactionId: this.getWorkflowManager().getTransaction().id,
                organizationName: name,
            },
        });
    }

    getBundle() {
        return this.bundle;
    }

    setBundle(bundle: Bundle) {
        this.bundle = bundle;
    }

    getClient() {
        return this.client;
    }

    getIsReadOnly() {
        return this.isReadOnly;
    }

    getCommandContext() {
        return this.commandContext;
    }

    getWorkflowManager() {
        return this.workflowManager;
    }

    async createPreviewImage(isThreeD?: boolean, resolution?: number): Promise<string> {
        const workflow = this.workflowManager.getWorkflow();
        const cameraOrientation = workflow?.finalizeStepConfig?.lookAtAnimation;
        if (isThreeD) {
            if (!cameraOrientation) {
                throw new Error("Failed to generate cart preview image!");
            }
            const data = await this.workflowManager
                .getPreviewService()
                ?.renderSceneScreenshot(resolution ?? 512, cameraOrientation);
            return data || "";
        }

        let res = 2048;
        if (resolution && resolution <= 2048) {
            res = resolution;
        }

        const canvas = createCanvas(res, res);

        // Resolve default layout for rendering.
        const layouts = this.commandContext.getAllLayouts();
        const defaultPanelIndex = workflow.defaultPreviewPanelIndex || 0;
        const targetPanel = workflow.panels[defaultPanelIndex];
        const defaultLayout = layouts.find((l) => l.layoutState?.layout.panelId === targetPanel?.name) || layouts[0];
        const region = defaultLayout.layoutState.layout.previewRegion
            ? {
                  x: defaultLayout.layoutState.layout.previewRegion.left,
                  y: defaultLayout.layoutState.layout.previewRegion.top,
                  width: defaultLayout.layoutState.layout.previewRegion.width,
                  height: defaultLayout.layoutState.layout.previewRegion.height,
              }
            : {
                  x: 0,
                  y: 0,
                  width: defaultLayout.layoutState.layout.width,
                  height: defaultLayout.layoutState.layout.height,
              };

        // Render the SVG for the target layout to a data URL for return.
        const layout = this.commandContext.getLayoutById(defaultLayout.layoutState.layout.id);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new ResourceGenerationError("Failed to obtain 2D context for preview image creation");
        }
        const svgElement = getSvgElement(layout.layoutState.layout, layout.layoutState.elements, {
            renderingConfiguration: {
                purpose: LayoutRenderingPurpose.Print,
                region: { left: region.x, top: region.y, width: region.width, height: region.height },
            },
        });
        const svgString = renderPapyrusComponentAsString(svgElement);
        const pith = await Pith.from(ctx as any, svgString, {
            anonymousCrossOrigin: true,
            ignoreDimensions: false,
            createCanvas: createCanvas as any,
            createImage: loadImage as any,
            DOMParser: getDomParser(),
            fetch: fetch,
        });
        await pith.render();
        const objectUrl = await this.getCanvasObjectURLAsync(canvas as any);
        return canvas.toDataURL(objectUrl as any);
    }

    getStepById(id: string) {
        const stepData = this.getWorkflowManager()
            .getWorkflow()
            .steps.find((step) => step.stepName === id);
        if (!stepData || !this.stepHasHandle(stepData)) return undefined;
        const cachedHandle = this.cachedStepHandles.get(stepData.stepName);
        if (cachedHandle) {
            return cachedHandle;
        }
        const createdHandle = StepHandleFactory.get(this.getWorkflowManager(), stepData);
        this.cachedStepHandles.set(stepData.stepName, createdHandle);
        return createdHandle;
    }

    getSteps(): StepHandle<AnyStepData>[] {
        return this.getScenes().flatMap((scene) => this.getStepsByScene(scene));
    }

    getStepsConditionallyActive(): StepHandle<AnyStepData>[] {
        return this.getScenes().flatMap((scene) =>
            this.getStepsByScene(scene).filter((step) =>
                stepConditionsAreSatisfied(step.getRaw(), this.getWorkflowManager().getStepSelections()),
            ),
        );
    }

    getScenes(): Scene[] {
        // Internally scenes are called step groups, but we expose them to the user as 'scenes'.
        const stepGroups = this.getWorkflowManager().getWorkflow().stepGroups;
        return stepGroups.map((sg) => {
            return {
                id: sg.id,
                name: sg.name,
                stepIds: sg.stepNames,
            } as Scene;
        });
    }

    getSelectionPriceSubunits(disablePriceBreaks?: boolean): number {
        const selections = this.getWorkflowManager().getWorkflowSelections();
        let totalSelectionCost = 0;
        Object.values(selections).forEach((step) => {
            totalSelectionCost += step.selections.reduce((acc, variant) => acc + (variant.priceModifier || 0), 0);
        });
        return this.getPriceSubUnitsAfterPriceBreaks(totalSelectionCost, disablePriceBreaks);
    }

    getBasePriceSubunits(includeAdditionalProduct?: boolean, disablePriceBreaks?: boolean): number {
        const basePrice = this.getPriceSubUnitsAfterPriceBreaks(
            this.getWorkflowManager().getProduct().basePrice || 0,
            disablePriceBreaks,
        );
        if (includeAdditionalProduct) {
            const additionalPrice = this.getAdditionalProductPriceSubunits(disablePriceBreaks) || 0;
            return basePrice + additionalPrice;
        }
        return basePrice;
    }

    getAdditionalProductPriceSubunits(disablePriceBreaks?: boolean): number | undefined {
        const integrationProduct = this.getWorkflowManager().getTransaction().integrationProduct;
        if (integrationProduct?.additionalIntegrationProduct?.product) {
            const additionalProductPrice = integrationProduct.additionalIntegrationProduct.product.basePrice || 0;
            return this.getPriceSubUnitsAfterPriceBreaks(additionalProductPrice, disablePriceBreaks);
        }
        return undefined;
    }

    getTotalPriceSubunits(disablePriceBreaks?: boolean): number {
        return (
            this.getBasePriceSubunits(false, disablePriceBreaks) + this.getSelectionPriceSubunits(disablePriceBreaks)
        );
    }

    private getPriceSubUnitsAfterPriceBreaks(subUnits: number, disablePriceBreaks?: boolean): number {
        const percentage = disablePriceBreaks ? 1 : this.priceBreakToBeApplied();
        const finalPrice = subUnits * percentage;
        return Math.ceil(finalPrice);
    }

    priceBreakToBeApplied(): number {
        // Sort price breaks from largest minQty to smallest.
        const priceBreaks = (this.getWorkflowManager().getProduct().priceBreaks || []).sort(
            (a, b) => -(a.minQty - b.minQty),
        );
        const matchingExperiences = [...this.getMatchingExperiencesFromBundle(), this];
        const totalQuantity = matchingExperiences.reduce((acc, exp) => acc + exp.getQuantity(), 0);
        for (const pb of priceBreaks) {
            if (pb.minQty <= totalQuantity) {
                return pb.percentage;
            }
        }
        return 1;
    }

    checkForPriceBreakChanges(): void {
        const updatedPriceBreak = this.priceBreakToBeApplied();
        if (this.currentPriceBreak !== updatedPriceBreak) {
            this.updatePriceBreak(updatedPriceBreak);
            this.getMatchingExperiencesFromBundle().forEach((wfe: WorkflowExperienceImpl) =>
                wfe.updatePriceBreak(updatedPriceBreak),
            );
        }
    }

    private getMatchingExperiencesFromBundle(): WorkflowExperience[] {
        return (this.bundle?.getWorkflowExperiences() || []).filter(
            (exp) =>
                exp.getWorkflowManager().getTransaction().id !== this.getWorkflowManager().getTransaction().id &&
                exp.getWorkflowManager().getProduct().id === this.getWorkflowManager().getProduct().id,
        );
    }

    private updatePriceBreak(value: number) {
        this.currentPriceBreak = value;
        this.callEvent(WorkflowExperienceEventType.PriceBreakChanged);
    }

    async copySelectionsViaGlobalConfiguration(
        bundle: Bundle,
        experience: WorkflowExperience,
        filter?: StepHandle<AnyStepData>[],
    ) {
        const bundleProperties = await bundle.getGlobalProperties();
        const filterIds = filter?.map((s) => s.getId());
        const targetSteps = this.getSteps().filter((s) => filterIds === undefined || filterIds.includes(s.getId()));
        const originatingSteps = experience.getSteps();

        const getGlobalPropertyFromConfig = (config: GlobalPropertyAspectConfiguration) => {
            return bundleProperties.find((p) => p.getRawProperty().name === config.aspectName);
        };

        targetSteps.forEach((targetStep) => {
            // Grab the properties of the step, stringify them for easier comparison
            const uniqueProperties = new Set<string>();
            const propertyConfigurations = targetStep.getRaw().globalPropertyAspectConfigurations;
            if (propertyConfigurations === undefined) return;
            propertyConfigurations.forEach((p) => uniqueProperties.add(JSON.stringify(p)));
            // Go through properties of originating experience and search for matches
            originatingSteps.forEach((originatingStep) => {
                const properties = originatingStep.getRaw().globalPropertyAspectConfigurations;
                if (properties === undefined) return;
                properties.forEach((p) => {
                    const actualProperty = getGlobalPropertyFromConfig(p);
                    const requiredProperty = uniqueProperties.has(JSON.stringify(p));
                    if (requiredProperty && actualProperty?.getType() === AspectType.Option) {
                        const curVariant = originatingStep.getCurrentVariant();
                        if (!curVariant) return;
                        targetStep.selectVariant(curVariant);
                        uniqueProperties.delete(JSON.stringify(p));
                    } else if (requiredProperty && actualProperty?.getType() === AspectType.Text) {
                        const text = (originatingStep as TextStepHandle).getText();
                        (targetStep as TextStepHandle).setText(text);
                        uniqueProperties.delete(JSON.stringify(p));
                    }
                    // TODO: File Upload, not required now.
                });
            });
        });
    }

    getStepByName(name: string) {
        const stepData = this.getWorkflowManager()
            .getWorkflow()
            .steps.find((step) => step.stepTitle === name);
        if (!stepData || !this.stepHasHandle(stepData)) return undefined;
        return StepHandleFactory.get(this.getWorkflowManager(), stepData);
    }

    getStepsByType(type: StepType): StepHandle<AnyStepData>[] {
        return this.getWorkflowManager()
            .getWorkflow()
            .steps.filter((step) => step.type === type)
            .map((step) => StepHandleFactory.get(this.getWorkflowManager(), step));
    }

    getStepsByScene(scene: Scene): StepHandle<AnyStepData>[] {
        // Enforce scene existance.
        if (
            !this.getWorkflowManager()
                .getWorkflow()
                .stepGroups.find((s) => s.name === scene.name)
        ) {
            throw new Error(
                "Given scene is not present on workflow! Be careful when persisting scenes that you only use them with the relevant workflow.",
            );
        }
        const steps = scene.stepIds.map(
            (id) =>
                this.getWorkflowManager()
                    .getWorkflow()
                    .steps.find((step) => step.stepName === id)!,
        );
        return steps
            .filter((s) => this.stepHasHandle(s))
            .map((step) => StepHandleFactory.get(this.getWorkflowManager(), step));
    }

    async attachCustomerDetails(details: { email: string }): Promise<void> {
        return this.assignCustomerDetails({ emailAddress: details.email });
    }

    async assignCustomerDetails(details: CustomerDetailsInput): Promise<void> {
        const response = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ transactionAddStakeholder: Transaction }>({
                mutation: addTransactionStakeholderMutation,
                variables: {
                    id: this.getWorkflowManager().getTransaction().id,
                    details,
                    type: "Owner",
                },
            });
        const stakeholder = response.data?.transactionAddStakeholder?.stakeholders?.find(
            (s) => s.customer?.emailAddress === details.emailAddress,
        );
        if (stakeholder?.customer) {
            this.getWorkflowManager().setTransactionCustomer(stakeholder.customer);
            // Persist the customer ID for this transaction so that we can retrieve it when authenticating on reload.
            const transactionCustomerIds = persistenceService.getMap("transactionCustomerIds") || new Map();
            transactionCustomerIds.set(this.getWorkflowManager().getTransaction().id, stakeholder.customer.id);
            persistenceService.setMap("transactionCustomerIds", transactionCustomerIds);
        }
    }

    attachRenderableSceneListener(cb: (scenes: RenderableScene[]) => void): void {
        this.renderableSceneCallbacks.push(cb);
        cb(this.renderableScenes);
    }

    detachRenderableSceneListener(cb: (scenes: RenderableScene[]) => void): void {
        this.renderableSceneCallbacks = this.renderableSceneCallbacks.filter((x) => x !== cb);
    }

    debouncedSavedDesignUpdate = debounce(async () => {
        const existingDesign = await designService.getSavedDesignByTransaction(
            this.getWorkflowManager().getTransaction().id,
        );
        if (existingDesign) this.save();
    }, 2500);

    async save(title?: string) {
        const state = this.getCommandContext().getState();
        if (!state) throw new UnhandledBehaviorError("State undefined!");
        const getTitle = async () => {
            if (title) return title;
            const transactionId = this.getWorkflowManager().getTransaction().id;
            const ds = await designService.getSavedDesigns();
            const existingTitle = ds.find((d) => d.transactionId === transactionId)?.title;
            return existingTitle ? existingTitle : `My design`;
        };
        const saveData: SavedDesign = {
            title: await getTitle(),
            thumbnail: await this.createPreviewImage(false, 256),
            transactionId: this.getWorkflowManager().getTransaction().id,
            productId: this.getWorkflowManager().getProduct().id,
            integrationProductId: this.getWorkflowManager().getTransaction().integrationProduct!.id,
            workflowName: this.getWorkflowManager().getWorkflow().name,
            workflowId: this.getWorkflowManager().getWorkflow().id,
            lastEdited: new Date(),
        };
        await designService.addDesign(saveData);
        return saveData;
    }

    async copy() {
        const state = cloneDeep(this.getCommandContext().getState());
        if (!state) throw new UnhandledBehaviorError("Internal state is undefined! Cannot copy experience!");
        const savedWorkflowState = JSON.stringify(state.transaction);
        const workflow = this.getWorkflowManager().getWorkflow();
        const integrationProductId = this.getWorkflowManager().getTransaction().integrationProduct?.id;
        if (!integrationProductId) throw new UnhandledBehaviorError("Integration product id is undefined!");
        const experience = await this.getClient().getWorkflowExperience({
            type: "integration",
            integrationProductId,
            workflowId: workflow.id,
            workflowState: savedWorkflowState,
        });
        return experience;
    }

    async onDesignFinished(
        onProgressUpdate?: DesignCreationProgressUpdate,
        capturePreviewImage: boolean = true,
    ): Promise<DesignCreationMessage> {
        const transaction = this.workflowManager.getTransaction();
        if (transaction.completed) {
            await this.graphQlClient().mutate({
                mutation: finalizeUpdateTransactionMutation,
                variables: {
                    transactionId: transaction.id,
                },
            });

            return {
                event: "",
                exportedData: {},
                lineItemImageUrl: "",
                optionsCost: 0,
                transactionId: transaction.id,
                workflowViewerLink: "",
                workflowViewerReadOnlyLink: "",
            };
        }
        return createDesign(
            this.workflowManager,
            this.workflowManager.getWorkflow(),
            this.workflowManager.getLayouts(),
            () => this.commandContext.getState()!, // Handle case where undefined (create a default state)
            this.workflowManager.getProduct(),
            transaction,
            this.workflowManager.getWorkflowSelections(),
            this.workflowManager.getWorkflow().name,
            onProgressUpdate
                ? onProgressUpdate
                : () => {
                      /* Noop to avoid changing createDesign interface */
                  },
            (show3D?: boolean) => {
                if (capturePreviewImage) {
                    return this.createPreviewImage(show3D, 1024);
                }
                return Promise.resolve(undefined);
            },
            this.workflowManager.getWorkflowMetadata(),
        );
    }

    /**
     * @param step The step to test against.
     * @returns Returns true when a valid handle is implemented for a given step type. Certain steps don't support handles, such as silent illustrations.
     */
    private stepHasHandle(step: Step<AnyStepData>): boolean {
        return step.type !== StepType.SilentIllustration && step.type !== StepType.ProductOverlay;
    }

    private getCanvasObjectURLAsync = async (canvas: HTMLCanvasElement): Promise<string> => {
        return new Promise((resolve, reject) => {
            try {
                canvas.toBlob((blob) => {
                    if (blob) {
                        const objectURL = URL.createObjectURL(blob);
                        resolve(objectURL);
                    }
                });
            } catch (e: any) {
                reject(e);
            }
        });
    };

    getExportedData(): Map<string, { [key: string]: string }> {
        const exportedMetadata: Map<string, { [key: string]: string }> = new Map();
        const metadata = this.getWorkflowManager().getWorkflowMetadata();
        const variantSelections = this.getWorkflowManager().getWorkflowSelections();

        // Process metadata
        Object.keys(metadata).forEach((stepId) => {
            const step = this.workflowManager.getWorkflow().steps.find((step) => step.stepName === stepId);
            if (!step) return;
            if (!exportedMetadata.has(step.stepTitle)) {
                exportedMetadata.set(step.stepTitle, {});
            }
            const stepMetadata = metadata[stepId];
            Object.keys(stepMetadata).forEach((metadataType) => {
                exportedMetadata.get(step.stepTitle)![metadataType] = stepMetadata[metadataType];
            });
        });

        // Process variant selections
        Object.keys(variantSelections)?.forEach((stepId) => {
            const step = this.workflowManager.getWorkflow().steps.find((step) => step.stepName === stepId);
            if (!step) return;
            if (!exportedMetadata.has(step.stepTitle)) {
                exportedMetadata.set(step.stepTitle, {});
            }
            exportedMetadata.get(step.stepTitle)!["selection"] = variantSelections[stepId].selections[0].name;
        });
        // Return the exported data
        return exportedMetadata;
    }

    getQuantity(): number {
        return this.getWorkflowManager().getTransaction().quantity || 1;
    }

    async setQuantity(quantity: number): Promise<void> {
        if (quantity < 1) {
            throw new RangeError("WorkflowExperience quantity must be greater than zero.");
        }
        if (this.isReadOnly) {
            throw new Error("Cannot update quantity on a read-only WorkflowExperience");
        }
        this.getWorkflowManager().getTransaction().quantity = quantity;
        this.callEvent(WorkflowExperienceEventType.QuantityChanged);
        this.checkForPriceBreakChanges();

        const transaction = this.getWorkflowManager().getTransaction();
        await this.graphQlClient().mutate({
            mutation: updateTransactionQuantityQuery,
            variables: {
                id: transaction.id,
                quantity,
            },
        });
    }

    addEventListener(
        type: WorkflowExperienceEventType,
        callback: (workflowExperience: WorkflowExperience) => void,
    ): void {
        const events = this.eventCallbacks.get(type) || [];
        events.push(callback);
        this.eventCallbacks.set(type, events);
    }

    removeEventListener(
        type: WorkflowExperienceEventType,
        callback: (workflowExperience: WorkflowExperience) => void,
    ): void {
        const events = this.eventCallbacks.get(type) || [];
        this.eventCallbacks.set(
            type,
            events.filter((evt) => evt !== callback),
        );
    }

    private callEvent(type: WorkflowExperienceEventType): void {
        (this.eventCallbacks.get(type) || []).forEach((cb) => cb(this));
    }
}
